/**
 * Roster import — pure functions behind the wizard: header detection, column auto-mapping
 * (zh/en synonyms, incl. the ones operator sheets actually use) and row normalization into the
 * contract's ImportRow. No DOM / no network, so vitest covers it directly.
 *
 * The server re-validates everything (SN shape, email, date) and classifies; the client only
 * trims, uppercases the SN, drops blank cells and turns spreadsheet dates into YYYY-MM-DD.
 */
import type { ImportRow } from '@/types';

export const MAX_IMPORT_ROWS = 5000;

export type ImportField = 'sn' | 'owner_name' | 'owner_email' | 'team' | 'notes' | 'expires_at';
export const IMPORT_FIELDS: ImportField[] = ['sn', 'owner_name', 'owner_email', 'team', 'notes', 'expires_at'];

export type ColumnMapping = Record<ImportField, number | null>;

// Normalized (see normHeader) synonyms. Exact matches win over substring matches.
const SYNONYMS: Record<ImportField, string[]> = {
  sn: ['序號', '硬體序號', '機器序號', '設備序號', '序列號', 'bios序號', 'biossn', 'sn', 'serial', 'serialnumber', 'serialno', 'bios'],
  owner_name: ['擁有人', '擁有者', '使用者', '使用人', '保管人', '姓名', 'owner', 'ownername', 'name', 'user', 'username'],
  owner_email: ['email', 'e-mail', 'mail', '信箱', '電子郵件', '電子信箱', '聯絡信箱', '郵件'],
  team: ['團隊', '部門', '單位', '組別', 'team', 'department', 'dept', 'group'],
  notes: ['備註', '說明', '註記', 'notes', 'note', 'remark', 'remarks', 'comment', 'memo'],
  expires_at: ['到期日', '到期', '歸還日', '截止日', '期限', 'expires', 'expiresat', 'expiry', 'expiration', 'expire', 'due'],
};

// A license column says 序號 too; it must never be read as the hardware SN.
const NOT_SN = ['軟體', '授權', 'license', 'licence', 'key'];

/** Lowercase, strip whitespace / underscores / most punctuation (keeps CJK and '-'). */
export function normHeader(h: string): string {
  return h
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s_()()::.,、/\\[\]【】「」*#]+/g, '');
}

function matchScore(field: ImportField, header: string): number {
  const h = normHeader(header);
  if (!h) return 0;
  if (field === 'sn' && NOT_SN.some((w) => h.includes(w))) return 0;
  const syn = SYNONYMS[field];
  if (syn.includes(h)) return 2;
  // Substring match only for distinctive synonyms: any CJK one, or ASCII of 4+ letters — so
  // 'sn' does not claim "snapshot" and 'due' does not claim "residue".
  if (syn.some((s) => (/[^\x00-\x7f]/.test(s) || s.length >= 4) && h.includes(s))) return 1;
  return 0;
}

/** Map each field to a column index (each column used once; SN first, exact before substring). */
export function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = { sn: null, owner_name: null, owner_email: null, team: null, notes: null, expires_at: null };
  const used = new Set<number>();
  for (const pass of [2, 1]) {
    for (const field of IMPORT_FIELDS) {
      if (mapping[field] !== null) continue;
      const idx = headers.findIndex((h, i) => !used.has(i) && matchScore(field, h) === pass);
      if (idx >= 0) {
        mapping[field] = idx;
        used.add(idx);
      }
    }
  }
  return mapping;
}

/** Excel-style column letter: 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(i: number): string {
  let s = '';
  let n = i;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Any spreadsheet cell → string. Dates use UTC getters: xlsx dates arrive as UTC midnight, and
 *  local getters would shift them a day in negative-offset zones. */
export function cellToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return '';
    return `${v.getUTCFullYear()}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())}`;
  }
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v);
}

/** 2026/1/5, 2026.01.05, 2026-01-05 00:00:00 → 2026-01-05. Anything else is passed through
 *  trimmed so the server can report it as invalid (rather than the client silently dropping it). */
export function normalizeDate(raw: string): string | undefined {
  const s = raw.trim();
  if (!s) return undefined;
  const m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ T].*)?$/);
  if (m) return `${m[1]}-${pad(Number(m[2]))}-${pad(Number(m[3]))}`;
  return s;
}

export interface SheetLayout {
  /** Index of the header row, or null when the file has no header (plain SN list). */
  headerRow: number | null;
  /** Column titles shown in the mapper (header text, or "欄 A" for a headerless file). */
  headers: string[];
  /** First data row index. */
  dataStart: number;
  mapping: ColumnMapping;
}

const isBlankRow = (r: string[]) => r.every((c) => c.trim() === '');

/** Header = first non-blank row when it auto-maps an SN column by name; otherwise the file is
 *  treated as headerless and column A is the SN (the v1 single-column CSV still imports). */
export function detectLayout(rows: string[][]): SheetLayout {
  const first = rows.findIndex((r) => !isBlankRow(r));
  const width = rows.reduce((w, r) => Math.max(w, r.length), 0);
  if (first >= 0) {
    const mapping = autoMapColumns(rows[first]);
    if (mapping.sn !== null) {
      const headers = Array.from({ length: width }, (_, i) => (rows[first][i] ?? '').trim() || `欄 ${columnLetter(i)}`);
      return { headerRow: first, headers, dataStart: first + 1, mapping };
    }
  }
  const headers = Array.from({ length: Math.max(width, 1) }, (_, i) => `欄 ${columnLetter(i)}`);
  return {
    headerRow: null,
    headers,
    dataStart: Math.max(first, 0),
    mapping: { sn: 0, owner_name: null, owner_email: null, team: null, notes: null, expires_at: null },
  };
}

export interface BuiltRows {
  rows: ImportRow[];
  /** 1-based sheet line of each built row (rows[i] came from line lines[i]). */
  lines: number[];
}

/** Turn data rows into ImportRow[] using the mapping. Rows whose mapped cells are all blank are
 *  dropped (trailing blank lines); a row with data but no SN is kept so the server flags it. */
export function buildImportRows(rows: string[][], mapping: ColumnMapping, dataStart: number): BuiltRows {
  const out: ImportRow[] = [];
  const lines: number[] = [];
  const cell = (r: string[], f: ImportField) => {
    const i = mapping[f];
    return i === null || i === undefined ? '' : (r[i] ?? '').trim();
  };
  for (let i = dataStart; i < rows.length; i++) {
    const r = rows[i];
    const vals = IMPORT_FIELDS.map((f) => cell(r, f));
    if (vals.every((v) => v === '')) continue;
    const row: ImportRow = { sn: cell(r, 'sn').toUpperCase() };
    const owner = cell(r, 'owner_name');
    const email = cell(r, 'owner_email');
    const team = cell(r, 'team');
    const notes = cell(r, 'notes');
    const exp = normalizeDate(cell(r, 'expires_at'));
    if (owner) row.owner_name = owner;
    if (email) row.owner_email = email;
    if (team) row.team = team;
    if (notes) row.notes = notes;
    if (exp) row.expires_at = exp;
    out.push(row);
    lines.push(i + 1);
  }
  return { rows: out, lines };
}

/** First n non-blank values of a column (the mapper's 前三列 sample). */
export function sampleColumn(rows: string[][], col: number | null, dataStart: number, n = 3): string[] {
  if (col === null) return [];
  const out: string[] = [];
  for (let i = dataStart; i < rows.length && out.length < n; i++) {
    const r = rows[i];
    if (isBlankRow(r)) continue;
    out.push((r[col] ?? '').trim() || '—');
  }
  return out;
}

/** Default batch name from the file name: "asus-q4-roster.xlsx" → "asus-q4-roster". */
export function defaultBatchName(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').slice(0, 80);
}
